import { useCallback, useEffect, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import { CircleCheck, Link2, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import type { CommentAttachmentDto } from '@inference.engine/workspace-api-client/web';
import { getInitials } from '@/shared/utils/getInitials';
import { PermissionTooltip } from '@/shared/ui/ScopedPermission/PermissionTooltip';
import { useCommentEditor } from '../../lib/useCommentEditor';
import { CommentInput } from '../CommentInput';
import { AttachmentChipList } from '../AttachmentChipList';
import type { CommentProps } from './Comment.types';
import { useTranslation } from 'react-i18next';

const EMPTY_ATTACHMENTS: CommentAttachmentDto[] = [];

export function Comment({
  authorName,
  authorAvatarUrl,
  avatarSlot,
  footerSlot,
  timestamp,
  content,
  attachments = EMPTY_ATTACHMENTS,
  resolved = false,
  replyCount = 0,
  selected = false,
  onReply,
  onEdit,
  onResolve,
  resolveLabel,
  resolveDisabled = false,
  onDelete,
  onCopyLink,
  onEditStart,
  onEditEnd,
  renderEditInput,
  renderReplyInput,
  pills,
  children,
}: CommentProps) {
  const { t } = useTranslation('projects');

  const [isEditing, setIsEditing] = useState(false);
  const [replyComposerResetKey, setReplyComposerResetKey] = useState(0);

  const editor = useCommentEditor({
    content,
    editable: false,
    onUpdate: () => {},
  });

  useEffect(() => {
    editor.commands.setContent(content, { emitUpdate: false });
  }, [editor, content]);

  const handleStartEdit = useCallback(() => {
    onEditStart?.();
    setIsEditing(true);
  }, [onEditStart]);

  const handleCancelEdit = useCallback(() => {
    onEditEnd?.();
    setIsEditing(false);
  }, [onEditEnd]);

  const handleEditSaved = useCallback(() => {
    onEditEnd?.();
    setIsEditing(false);
  }, [onEditEnd]);

  const handleReply = useCallback(
    async (replyContent: Record<string, unknown>) => {
      await onReply?.(replyContent, []);
    },
    [onReply],
  );

  const handleEditSave = useCallback(
    async (editedContent: Record<string, unknown>) => {
      await onEdit?.(editedContent, []);
      onEditEnd?.();
      setIsEditing(false);
    },
    [onEdit, onEditEnd],
  );

  const handleCancelReply = useCallback(() => {
    setReplyComposerResetKey((k) => k + 1);
  }, []);

  const canEdit = !!onEdit || !!renderEditInput;
  const canReply = !!onReply || !!renderReplyInput;
  const showActions = !isEditing && (!!onResolve || canEdit || !!onCopyLink || !!onDelete);
  const showMenu = !isEditing && (canEdit || !!onCopyLink || !!onDelete);
  const showReplies = replyCount > 0;
  const showPillRow = (!isEditing && !!pills) || showReplies;
  const showReplySection = selected && !isEditing && (canReply || !!children);
  const resolveName = resolveLabel ?? t('projectComment.comment.approve');

  return (
    <div data-testid="comment" className="group flex w-full flex-col gap-3">
      <div className="flex gap-3">
        {avatarSlot ?? (
          <Avatar size="mini" roundness="full" className="mt-1 shrink-0">
            {authorAvatarUrl && <AvatarImage src={authorAvatarUrl} alt={authorName} />}
            <AvatarFallback size="mini" roundness="full">
              {getInitials(authorName)}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{authorName}</span>
              <span className="text-xs text-muted-foreground">{timestamp}</span>
            </div>
            {showActions && (
              <div className="flex items-center gap-1">
                {showMenu && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="mini"
                        aria-label={t('projectComment.comment.commentActions')}
                        onClick={(e) => e.stopPropagation()}
                        className="invisible group-hover:visible group-has-focus-visible:visible data-[state=open]:visible"
                      >
                        <MoreVertical className="size-md text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <DropdownMenuItem onSelect={handleStartEdit}>
                          <Pencil className="mr-2 size-md" />
                          {t('projectComment.comment.edit')}
                        </DropdownMenuItem>
                      )}
                      {onCopyLink && (
                        <DropdownMenuItem onSelect={onCopyLink}>
                          <Link2 className="mr-2 size-md" />
                          {t('projectComment.comment.copyLink')}
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem onSelect={onDelete}>
                          <Trash2 className="mr-2 size-md" />
                          {t('projectComment.comment.delete')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {onResolve && (
                  <PermissionTooltip disabled={resolveDisabled}>
                    <Button
                      variant="ghost"
                      size="mini"
                      aria-label={resolveName}
                      title={resolveName}
                      disabled={resolveDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        onResolve();
                      }}
                      className="group-focus-within:visible"
                    >
                      <CircleCheck
                        className={clsx(
                          resolved
                            ? 'size-md [&_circle]:fill-positive-foreground [&_circle]:stroke-positive-foreground [&_path]:stroke-white'
                            : 'size-md text-muted-foreground',
                        )}
                      />
                    </Button>
                  </PermissionTooltip>
                )}
              </div>
            )}
          </div>

          {isEditing && canEdit ? (
            <div className="min-w-0">
              {renderEditInput ? (
                renderEditInput({
                  initialContent: content,
                  initialAttachments: attachments,
                  onCancel: handleCancelEdit,
                  onSaved: handleEditSaved,
                })
              ) : (
                <CommentInput
                  placeholder={t('projectComment.comment.editYourComment')}
                  initialContent={content}
                  submitLabel={t('projectComment.comment.save')}
                  onSave={handleEditSave}
                  leftActions={({ isSubmitting }) => (
                    <Button
                      aria-label={t('projectComment.comment.cancel')}
                      size="sm"
                      variant="outline"
                      disabled={isSubmitting}
                      onClick={handleCancelEdit}
                    >
                      {t('projectComment.comment.cancel')}
                    </Button>
                  )}
                />
              )}
            </div>
          ) : (
            <>
              <EditorContent editor={editor} className="min-w-0 wrap-break-word" />
              {attachments.length > 0 && <AttachmentChipList attachments={attachments} />}
            </>
          )}

          {!isEditing && footerSlot}

          {showPillRow && (
            <div className="flex flex-wrap items-center gap-1.5">
              {!isEditing && pills}
              {showReplies && (
                <span
                  data-testid="comment-replies-count"
                  className="px-1 text-primary type-body-sm-strong"
                >
                  {t('projectComment.comment.replyCount', { count: replyCount })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {showReplySection && <Separator />}

      {children}

      {selected && canReply && !isEditing && (
        <div className="flex-1 min-w-0">
          {renderReplyInput ? (
            renderReplyInput()
          ) : (
            <CommentInput
              key={replyComposerResetKey}
              placeholder={t('projectComment.comment.leaveYourReply')}
              submitLabel={t('projectComment.comment.reply')}
              onSave={handleReply}
              leftActions={({ isSubmitting }) => (
                <Button
                  aria-label={t('projectComment.comment.cancelReply')}
                  size="sm"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={handleCancelReply}
                >
                  {t('projectComment.comment.cancel')}
                </Button>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}
